import React, { useState } from "react";
import { Box, Menu, MenuItem, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useKivunovaTranslation } from "@kivunova/kivufrontendcommon";
import CommonLoader from "../../common/CommonLoader/CommonLoader";
import AppButton from "../../common/AppButton";
import StudentForm from "./StudentForm";
import StudentExitDialog, { StudentExitAction } from "./StudentExitDialog";
import {
  useGraduateStudent,
  useStudents,
  useTransferOutStudent,
  useWithdrawStudent
} from "../../../hooks/api/education/useStudent";
import { Student, StudentStatus } from "./types/student";

const isTerminalStatus = (status?: StudentStatus | null) =>
  status === StudentStatus.WITHDRAWN ||
  status === StudentStatus.GRADUATED ||
  status === StudentStatus.TRANSFERRED_OUT;

const StudentList: React.FC = () => {
  const { t } = useKivunovaTranslation();
  const navigate = useNavigate();
  const [openForm, setOpenForm] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [exitTarget, setExitTarget] = useState<Student | null>(null);
  const [exitAction, setExitAction] = useState<StudentExitAction>("withdraw");
  const [exitMenuAnchor, setExitMenuAnchor] = useState<null | HTMLElement>(
    null
  );
  const [exitMenuStudent, setExitMenuStudent] = useState<Student | null>(null);
  const { data: students = [], isPending, refetch } = useStudents({ size: 50 });
  const withdrawMutation = useWithdrawStudent();
  const graduateMutation = useGraduateStudent();
  const transferMutation = useTransferOutStudent();

  const exitPending =
    withdrawMutation.isPending ||
    graduateMutation.isPending ||
    transferMutation.isPending;

  const openExit = (student: Student, action: StudentExitAction) => {
    setExitTarget(student);
    setExitAction(action);
    setExitMenuAnchor(null);
    setExitMenuStudent(null);
  };

  const runExit = async (reason: string) => {
    if (!exitTarget?.id) {
      return;
    }
    const input = {
      studentId: exitTarget.id,
      expectedVersion: exitTarget.version ?? undefined,
      reason: reason || undefined
    };
    if (exitAction === "withdraw") {
      await withdrawMutation.mutateAsync(input);
    } else if (exitAction === "graduate") {
      await graduateMutation.mutateAsync(input);
    } else {
      await transferMutation.mutateAsync(input);
    }
    setExitTarget(null);
    refetch();
  };

  if (isPending) {
    return (
      <CommonLoader
        loadingMessage={t("loading-students", "Loading students...")}
      />
    );
  }

  return (
    <Box data-testid="student-list-page" sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2
        }}
      >
        <Typography variant="h5" component="h1">
          {t("students-title", "Students")}
        </Typography>
        <AppButton onClick={() => setOpenForm(true)}>
          {t("add-student", "Add student")}
        </AppButton>
      </Box>

      <StudentExitDialog
        open={exitTarget != null}
        action={exitAction}
        studentName={exitTarget?.fullName ?? ""}
        loading={exitPending}
        onClose={() => setExitTarget(null)}
        onConfirm={runExit}
      />

      <Menu
        anchorEl={exitMenuAnchor}
        open={Boolean(exitMenuAnchor)}
        onClose={() => {
          setExitMenuAnchor(null);
          setExitMenuStudent(null);
        }}
      >
        <MenuItem
          onClick={() =>
            exitMenuStudent && openExit(exitMenuStudent, "withdraw")
          }
        >
          {t("withdraw-student", "Withdraw")}
        </MenuItem>
        <MenuItem
          onClick={() =>
            exitMenuStudent && openExit(exitMenuStudent, "graduate")
          }
        >
          {t("graduate-student", "Graduate")}
        </MenuItem>
        <MenuItem
          onClick={() =>
            exitMenuStudent && openExit(exitMenuStudent, "transferOut")
          }
        >
          {t("transfer-student", "Transfer out")}
        </MenuItem>
      </Menu>

      {students.length === 0 ? (
        <Typography color="text.secondary">
          {t(
            "students-empty",
            "No students yet. Add your first student to get started."
          )}
        </Typography>
      ) : (
        <Box component="ul" sx={{ m: 0, p: 0, listStyle: "none" }}>
          {students.map((student) => (
            <Box
              component="li"
              key={student.id}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                py: 1.5,
                borderBottom: "1px solid",
                borderColor: "divider",
                opacity: isTerminalStatus(student.status) ? 0.65 : 1
              }}
            >
              <Box>
                <Typography fontWeight={600}>{student.fullName}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {student.studentCode}
                  {student.status ? ` · ${student.status}` : ""}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 1 }}>
                <AppButton
                  variant="secondary"
                  onClick={() => navigate(`students/${student.id}`)}
                >
                  {t("view", "View")}
                </AppButton>
                {!isTerminalStatus(student.status) ? (
                  <>
                    <AppButton
                      variant="text"
                      onClick={() => {
                        setSelectedStudent(student);
                        setOpenForm(true);
                      }}
                    >
                      {t("edit", "Edit")}
                    </AppButton>
                    <AppButton
                      variant="text"
                      onClick={(event) => {
                        setExitMenuAnchor(event.currentTarget);
                        setExitMenuStudent(student);
                      }}
                    >
                      {t("end-enrollment", "End enrollment")}
                    </AppButton>
                  </>
                ) : null}
              </Box>
            </Box>
          ))}
        </Box>
      )}

      <StudentForm
        open={openForm}
        onClose={() => {
          setOpenForm(false);
          setSelectedStudent(null);
        }}
        onSaved={() => refetch()}
        student={selectedStudent}
      />
    </Box>
  );
};

export default StudentList;
